// components/AdminMessaging/MessageInbox.tsx
// Main inbox view for admin messaging

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchPendingMessages,
    formatRelativeTime,
} from '../../services/adminMessaging';
import type { AdminIncomingMessage, AdminMessageQueueFilter } from '../../types';
import './AdminMessaging.css';

type FilterStatus = 'pending' | 'responded' | 'archived' | 'all';

export const MessageInbox: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [messages, setMessages] = useState<AdminIncomingMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState<FilterStatus>('pending');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // Search is applied on demand (Enter / button), not on every keystroke,
    // so read it through a ref to keep it out of the auto-load effect deps.
    const searchQueryRef = useRef(searchQuery);
    searchQueryRef.current = searchQuery;


    const loadMessages = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const filterOptions: AdminMessageQueueFilter = {
                status: filter === 'all' ? undefined : [filter],
                page: currentPage,
                pageSize: 20,
                sortBy: 'sentAt',
                sortDirection: 'desc',
                searchQuery: searchQueryRef.current || undefined,
            };

            const result = await fetchPendingMessages(filterOptions);
            setMessages(result.messages);
            setTotalPages(result.totalPages);
            setTotalCount(result.totalCount);
        } catch (err: any) {
            console.error('Failed to load messages:', err);
            setError(err.message || 'Failed to load messages');
        } finally {
            setLoading(false);
        }
    }, [filter, currentPage]);

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    const handleSearch = () => {
        setCurrentPage(0);
        loadMessages();
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const getStatusCounts = () => {
        // This would ideally come from the API
        return {
            pending: messages.filter(m => m.status === 'pending').length,
            responded: messages.filter(m => m.status === 'responded').length,
            archived: messages.filter(m => m.status === 'archived').length,
        };
    };

    const statusCounts = getStatusCounts();

    if (loading && messages.length === 0) {
        return (
            <div className="messaging-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading messages...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="messaging-container">
            <header className="messaging-header">
                <div className="header-content">
                    <div>
                        <h1>💬 Admin Messaging</h1>
                        <p className="header-subtitle">
                            Messages from users to Omore
                        </p>
                    </div>
                    <div className="header-actions">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="back-button"
                        >
                            ← Back to Dashboard
                        </button>
                        <button
                            onClick={() => navigate('/admin-messaging/new')}
                            className="primary-button"
                        >
                            📤 Send New Message
                        </button>
                        <button
                            onClick={() => navigate('/admin-messaging/templates')}
                            className="secondary-button"
                        >
                            📝 Templates
                        </button>
                        <button
                            onClick={() => navigate('/admin-messaging/analytics')}
                            className="secondary-button"
                        >
                            📊 Analytics
                        </button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="messaging-main">
                {error && (
                    <div className="error-banner">
                        <span>⚠️ {error}</span>
                        <button onClick={loadMessages} className="retry-button">
                            Retry
                        </button>
                    </div>
                )}

                {/* Filters and Search */}
                <div className="inbox-controls">
                    <div className="filter-buttons">
                        <button
                            className={`filter-button ${filter === 'pending' ? 'active' : ''}`}
                            onClick={() => { setFilter('pending'); setCurrentPage(0); }}
                        >
                            📨 Pending
                            {filter !== 'pending' && statusCounts.pending > 0 && (
                                <span className="badge">{statusCounts.pending}</span>
                            )}
                        </button>
                        <button
                            className={`filter-button ${filter === 'responded' ? 'active' : ''}`}
                            onClick={() => { setFilter('responded'); setCurrentPage(0); }}
                        >
                            ✅ Responded
                        </button>
                        <button
                            className={`filter-button ${filter === 'archived' ? 'active' : ''}`}
                            onClick={() => { setFilter('archived'); setCurrentPage(0); }}
                        >
                            📁 Archived
                        </button>
                        <button
                            className={`filter-button ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => { setFilter('all'); setCurrentPage(0); }}
                        >
                            📋 All Messages
                        </button>
                    </div>

                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="Search by explorer name or message..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            className="search-input"
                        />
                        <button onClick={handleSearch} className="search-button">
                            🔍 Search
                        </button>
                    </div>
                </div>

                {/* Message Count */}
                <div className="message-count">
                    {totalCount > 0 ? (
                        <span>
                            Showing {messages.length} of {totalCount} message{totalCount !== 1 ? 's' : ''}
                        </span>
                    ) : (
                        <span>No messages found</span>
                    )}
                    <button onClick={loadMessages} className="refresh-button" disabled={loading}>
                        {loading ? '⏳' : '🔄'} Refresh
                    </button>
                </div>

                {/* Message List */}
                <div className="message-list">
                    {messages.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">📭</div>
                            <h3>No messages to display</h3>
                            <p>
                                {filter === 'pending' ? 'All caught up! No pending messages.' :
                                 filter === 'responded' ? 'No responded messages yet.' :
                                 filter === 'archived' ? 'No archived messages.' :
                                 'No messages found.'}
                            </p>
                        </div>
                    ) : (
                        messages.map((message) => (
                            <div
                                key={message.id}
                                className={`message-card ${message.status} ${
                                    !message.readByAdminAt ? 'unread' : ''
                                }`}
                                onClick={() => navigate(`/admin-messaging/message/${message.id}`)}
                            >
                                <div className="message-card-header">
                                    <div className="message-sender">
                                        <span className="explorer-icon">👤</span>
                                        <span className="explorer-name">
                                            {message.fromExplorerName}
                                        </span>
                                    </div>
                                    <span className="message-time">
                                        {formatRelativeTime(message.sentAt)}
                                    </span>
                                </div>

                                <p className="message-preview">
                                    {message.message.length > 150
                                        ? `${message.message.substring(0, 150)}...`
                                        : message.message}
                                </p>

                                <div className="message-card-footer">
                                    <div className="message-badges">
                                        <span className={`status-badge ${message.status}`}>
                                            {message.status === 'pending' ? '📨' :
                                             message.status === 'responded' ? '✅' : '📁'}
                                            {' '}
                                            {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
                                        </span>
                                        {!message.readByAdminAt && message.status === 'pending' && (
                                            <span className="unread-badge">● New</span>
                                        )}
                                        {!message.moderationPassed && (
                                            <span className="moderation-flag">
                                                ⚠️ Flagged
                                            </span>
                                        )}
                                    </div>
                                    <span className="view-details">
                                        View Details →
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination">
                        <button
                            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                            disabled={currentPage === 0 || loading}
                            className="pagination-button"
                        >
                            ← Previous
                        </button>
                        <span className="pagination-info">
                            Page {currentPage + 1} of {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                            disabled={currentPage >= totalPages - 1 || loading}
                            className="pagination-button"
                        >
                            Next →
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
};

export default MessageInbox;
